/**
 * @file Auth hooks — login, logout, текущий пользователь
 * @see docs/tz/00-architecture.md §7.2 Session Management
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';

export interface User {
  userId: string;
  email: string;
  name: string;
  roles: string[];
  permissions: string[];
  lastLoginAt: string | null;
}

/** Текущий пользователь (GET /api/auth/me) */
export function useCurrentUser() {
  return useQuery<User>({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      const { data } = await api.get('/auth/me');
      return data;
    },
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/** Login (POST /api/auth/login) */
export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (credentials: { email: string; password: string }) => {
      const { data } = await api.post('/auth/login', credentials);
      localStorage.setItem('access_token', data.accessToken);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });
}

/** Logout (POST /api/auth/logout) */
export function useLogout() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.post('/auth/logout');
    },
    onSettled: () => {
      localStorage.removeItem('access_token');
      queryClient.clear();
      // BroadcastChannel sync — logout во всех вкладках
      try {
        const bc = new BroadcastChannel('auth');
        bc.postMessage({ type: 'logout' });
        bc.close();
      } catch {
        // BroadcastChannel не поддерживается
      }
      navigate('/login');
    },
  });
}

/** Проверка permission у текущего пользователя */
export function useHasPermission(permission: string): boolean {
  const { data: user } = useCurrentUser();
  return user?.permissions.includes(permission) ?? false;
}
